"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Heart,
  CreditCard,
  Trash2,
  HeartCrack,
  ShoppingBag,
} from "lucide-react";
import { formatPrice } from "@/lib/utils";
import {
  getWishlist,
  removeFromWishlist,
  clearWishlist,
} from "@/lib/wishlist-manager";

import { toast } from "sonner";
import { addToCart } from "@/lib/cart-manager";
import Link from "next/link";
import { cn } from "@/lib/utils";

function WishlistItem({ data, onWishlistUpdate, onAddToCart }) {
  const t = useTranslations("wishlist");

  function handleRemoveFromWishlist() {
    removeFromWishlist(data.id_product);
    onWishlistUpdate();
  }

  return (
    <div className="flex w-full items-center justify-between gap-4 border-b border-gray-200 py-4">
      <div className="flex items-center gap-4">
        <Image
          src={`${data.main_image}`}
          width={60}
          height={60}
          alt={data.product_name}
          className="size-[60px] object-cover"
        />
        <div>
          <p className="font-semibold">{data.product_name}</p>
          <p className="text-base font-medium">{formatPrice(data.price)}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Link href="/order">
          <Button size="icon">
            <CreditCard />
          </Button>
        </Link>
        <Button size="icon" variant="outline" onClick={() => onAddToCart(data)}>
          <ShoppingBag />
        </Button>
        <Button size="icon" variant="ghost" onClick={handleRemoveFromWishlist}>
          <HeartCrack />
        </Button>
      </div>
    </div>
  );
}

export default function Wishlist({ scrolled }) {
  const t = useTranslations("wishlist");
  const [wishlist, setWishlist] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const updateWishlist = () => setWishlist(getWishlist());
    updateWishlist();

    window.addEventListener("wishlistUpdate", updateWishlist);
    return () => window.removeEventListener("wishlistUpdate", updateWishlist);
  }, []);

  if (!wishlist) return null;

  const handleWishlistUpdate = () => setWishlist(getWishlist());

  const handleWishlistClear = () => {
    clearWishlist();
    setWishlist([]);
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(t("addToCartSuccess"));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          data-count={wishlist.length}
          variant="ghost"
          size="icon"
          className={cn(
            "relative before:absolute before:right-1.5 before:top-0 before:aspect-square before:w-5 before:rounded-full before:bg-white before:p-0.5 before:text-xs before:text-muted-foreground before:content-[attr(data-count)]",
            scrolled
              ? "text-white hover:text-black"
              : "text-black hover:text-black",
          )}
          onClick={() => setIsOpen(true)}
        >
          <Heart className="text-xs font-medium" />
        </Button>
      </DialogTrigger>
      <DialogContent className="md:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          {wishlist.length !== 0 && (
            <div className="flex items-center justify-between font-normal">
              <Button
                size="sm"
                variant="ghost"
                className="gap-1 text-destructive"
                onClick={handleWishlistClear}
              >
                <Trash2 className="size-4" /> {t("emptyWishlist")}
              </Button>
              <div className="text-sm text-muted-foreground">{`${wishlist.length} ${t("item", { count: wishlist.length })}`}</div>
            </div>
          )}
        </DialogHeader>
        <ScrollArea className="mt-4 max-h-[60vh]">
          <div className="flex flex-col gap-2">
            {wishlist.map((item) => (
              <WishlistItem
                key={item.id_product}
                data={item}
                onWishlistUpdate={handleWishlistUpdate}
                onAddToCart={handleAddToCart}
              />
            ))}
            {wishlist.length === 0 && (
              <div className="flex w-full flex-col items-center py-20 font-semibold text-muted">
                <Heart className="h-[60px] w-[60px] text-muted" />
                <p className="mt-4">{t("emptyState")}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
